import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faChevronDown,
} from "@fortawesome/free-solid-svg-icons";
import supportedCountries from "../../assets/countryLists";

const CountryValidation = ({ value, userCountry, onChange }) => {
  const [isOpen, setIsOpen] = useState(!value);

  useEffect(() => {
    if (!userCountry || value) return;

    const supported = supportedCountries.find(
      (c) => c.code === userCountry || c.name === userCountry,
    );
    const countryCodeToFlag = (code = "") =>
      code
        .toUpperCase()
        .replace(/./g, (char) =>
          String.fromCodePoint(127397 + char.charCodeAt()),
        );

    if (supported) {
      onChange(supported);
      setIsOpen(false);
    }
  }, [userCountry, value, onChange]);

  const handleSelect = (country) => {
    onChange(country);
    setIsOpen(false);
  };

  return (
    <div className="bg-gray-800 rounded-xl border border-orange-500/30">
      {/* Selected Country (Collapsed State) */}
      {value && !isOpen && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="w-full p-2 flex items-left gap-3 hover:bg-gray-700 transition"
        >
          <div className="flex-1 text-left">
            <p className=" text-white font-normal">{value.name}</p>
            <p className="text-sm text-gray-400">{value.phoneCode}</p>
          </div>
          <FontAwesomeIcon icon={faChevronDown} className="text-gray-400" />
        </button>
      )}

      {/* Expanded Picker */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="p-6"
          >
            <div className="flex items-left gap-3 mb-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="text-red-400"
              />
              <div>
                <p className="text-sm text-gray-400">
                  Please select a supported country to continue.
                </p>
              </div>
            </div>

            <div className="space-y-3 max-h-60 overflow-y-auto text-left">
              {supportedCountries.map((country) => {
                const isSelected = value?.code === country.code;

                return (
                  <motion.div
                    key={country.code}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handleSelect(country)}
                    className={`p-3 rounded-lg border cursor-pointer transition ${
                      isSelected
                        ? "bg-orange-500/20 border-orange-500"
                        : "bg-gray-700/50 border-gray-600 hover:border-orange-400/50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{country.flag}</span>
                      <div className="flex-1">
                        <p className="font-medium text-white">{country.name}</p>
                        <p className="text-sm text-gray-400">
                          {country.phoneCode}
                        </p>
                      </div>
                      {isSelected && (
                        <div className="w-2 h-2 bg-orange-500 rounded-full" />
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CountryValidation;
